/* eslint-disable max-len */
import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { UploadComponent } from 'gw-capability-document-react';
import { useAuthentication } from 'gw-digital-auth-react';
import messages from './AddInvoicePopup.messages';
import metadata from './AddInvoicePopup.metadata.json5';
// eslint-disable-next-line no-unused-vars
import styles from './AddInvoicePopup.module.scss';

function AddInvoicePopup(props) {
    const [invoiceVM, setInvoiceVM] = useState({});
    const [showVatExempt, setVatExempt] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [lineItemVatData, setLineItemVatData] = useState([]);
    const [totalInclAmount, setTotalInclAmount] = useState(Number(0));
    const [invoiceValidated, setInvoiceValidated] = useState(false);
    const [invoiceDocValidated, setInvoiceDocValidated] = useState(false);
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestID,
        vatCustomer,
        requestKind,
        existingDocuments,
        // eslint-disable-next-line react/prop-types
        serviceRequestDocumentType,
        invoiceData
    } = props;

    useEffect(() => {
        setInvoiceVM(
            viewModelService.create(
                invoiceData,
                'cc',
                'edge.capabilities.servicerequest.statement.invoice.dto.ServiceRequestInvoiceDTO'
            )
        );
        setLineItemVatData([]);
    }, [invoiceData, viewModelService]);

    const getCurrencyValue = useCallback((rowData, index, property) => {
        // eslint-disable-next-line prefer-template
        return 'R' + rowData[property.id];
    }, []);

    const calculateEXclVatAmount = useCallback((value1) => {
        let total;
        if (requestKind === 'salvage' || (vatCustomer && !showVatExempt)) {
            const upper = Number(value1) * Number(100);
            const exclValue = upper / Number(115);
            total = parseFloat(exclValue).toFixed(2);
        } else {
            total = value1;
        }
        const inclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return inclusiveVatMount;
    }, [requestKind, showVatExempt, vatCustomer]);

    const calculateEXclVatAmountForExempt = useCallback((value1, value) => {
        let total;
        if (requestKind === 'salvage' || (vatCustomer && !value)) {
            const upper = Number(value1) * Number(100);
            const exclValue = upper / Number(115);
            total = parseFloat(exclValue).toFixed(2);
        } else {
            total = value1;
        }
        const inclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return inclusiveVatMount;
    }, [requestKind, vatCustomer]);

    const calculateVatAmount = useCallback((value1, value2) => {
        const vat = parseFloat(Number(value1) - Number(value2)).toFixed(2);

        const vatAmount = {
            amount: vat,
            currency: 'rand'
        };
        return vatAmount;
    }, []);

    const calculateVat = useCallback((value1, value2) => {
        const total = Number(value2) - Number(value1);
        const exclusiveVatMount = {
            amount: total,
            currency: 'rand'
        };
        return exclusiveVatMount;
    }, []);

    const checkInvoiceValidationStatus = useCallback(() => {
        const referenceNumber = _.get(invoiceVM, 'referenceNumber.value', undefined);
        const lineItems = _.get(invoiceVM, 'lineItemData_EXT.value', []);
        setInvoiceValidated(!_.isNil(referenceNumber) && lineItems.length >= 1);
    }, [invoiceVM, setInvoiceValidated]);

    const clearLineItemData = useCallback(() => {
        _.set(invoiceVM, 'description_EXT.value', undefined);
        _.set(invoiceVM, 'vat_Excl_EXT.value', undefined);
        _.set(invoiceVM, 'vat_EXT.value', undefined);
        _.set(invoiceVM, 'vat_incl_EXT.value', undefined);
        _.set(invoiceVM, 'vATExempt.value', undefined);
        setVatExempt(false);
    }, [invoiceVM]);

    const addNewLineItem = useCallback(() => {
        const description = _.get(invoiceVM.value, 'description_EXT');
        const vatExclAmount = _.get(invoiceVM.value, 'vat_Excl_EXT.amount', 0);
        const vatAmount = _.get(invoiceVM.value, 'vat_EXT.amount', 0);
        const vatInclAmount = _.get(invoiceVM.value, 'vat_incl_EXT.amount', 0).toFixed(2);
        if (description && vatExclAmount > 0 && vatInclAmount > 0) {
            const lineItem = {
                description, vatExclAmount, vatAmount, vatInclAmount
            };
            let total = Number(totalInclAmount) + Number(vatInclAmount);
            total = +total.toFixed(2);
            setTotalInclAmount(total);
            lineItemVatData.push(lineItem);
            _.set(invoiceVM.value, 'totalInclAmount', totalInclAmount);
            _.set(invoiceVM, 'lineItemData_EXT.value', lineItemVatData);
            checkInvoiceValidationStatus();
            clearLineItemData();
            refreshData();
        }
    }, [checkInvoiceValidationStatus, clearLineItemData, invoiceVM, lineItemVatData, refreshData, totalInclAmount]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(invoiceVM.value, path, value);
            if (path === 'vat_incl_EXT') {
                if (!_.isUndefined(value)) {
                    const referenceNum = _.get(invoiceVM, 'referenceNumber');
                    _.set(invoiceVM, 'description', referenceNum);
                    // eslint-disable-next-line max-len
                    const exclusiveVatMount = calculateEXclVatAmount(value.amount);
                    _.set(invoiceVM.value, 'vat_Excl_EXT', exclusiveVatMount);

                    const vatAmount = calculateVatAmount(value.amount, exclusiveVatMount.amount);
                    _.set(invoiceVM.value, 'vat_EXT', vatAmount);
                }
            } else if (path === 'vat_EXT') {
                const vatInclAmount = _.get(invoiceVM.value, 'vat_Excl_EXT.amount', 0);
                if (!_.isUndefined(value)) {
                    const exclusiveAmt = calculateVat(value.amount, vatInclAmount);
                    _.set(invoiceVM.value, 'vat_Excl_EXT', exclusiveAmt);
                }
            }
            checkInvoiceValidationStatus();
            refreshData();
        },
        [calculateEXclVatAmount, calculateVat, calculateVatAmount, checkInvoiceValidationStatus, invoiceVM, refreshData]
    );

    // eslint-disable-next-line no-unused-vars
    const handleVatExempt = useCallback((value, path) => {
        setVatExempt(!showVatExempt);
        const inclVatAmt = _.get(invoiceVM, 'vat_incl_EXT');
        // eslint-disable-next-line max-len
        if (inclVatAmt) {
            if (value) {
                const exclusiveVatMount = inclVatAmt;
                // eslint-disable-next-line max-len
                const vatAmt = calculateVatAmount(inclVatAmt.amount.value, exclusiveVatMount.amount.value);
                _.set(invoiceVM, 'vat_EXT.value', vatAmt);
                _.set(invoiceVM, 'vat_Excl_EXT.value', exclusiveVatMount);
            } else {
                // eslint-disable-next-line max-len
                const exclusiveVatMount = calculateEXclVatAmountForExempt(inclVatAmt.amount.value, value);
                // eslint-disable-next-line max-len
                const vatAmt = calculateVatAmount(inclVatAmt.amount.value, exclusiveVatMount.amount);
                _.set(invoiceVM, 'vat_EXT.value', vatAmt);
                _.set(invoiceVM, 'vat_Excl_EXT.value', exclusiveVatMount);
            }
        }
        refreshData();
    // eslint-disable-next-line max-len
    }, [calculateEXclVatAmountForExempt, calculateVatAmount, invoiceVM, refreshData, showVatExempt]);

    const handleAddInvoice = useCallback(() => {
        // eslint-disable-next-line no-unused-expressions
        addNewLineItem();
        onResolve(invoiceVM);
    }, [addNewLineItem, invoiceVM, onResolve]);

    const handleClose = useCallback(() => {
        setTotalInclAmount('');
        onReject();
    }, [onReject]);

    const handleInvoiceVMChange = useCallback((newModel) => {
        setInvoiceVM(newModel);
        refreshData();
    }, [refreshData]);

    if (_.isEmpty(invoiceVM)) {
        return null;
    }

    const getDocumentType = () => {
        switch (serviceRequestDocumentType) {
            case 'Fee_invoice':
                return 'Fee Invoice';
            case 'Reinstatement_Invoice':
                return 'Reinstatement Invoice';
            case 'Repair_Invoice':
                return 'Repair Invoice';
            case 'Salvage_Tender_Documents':
                return 'Salvage Tender Documents';
            default:
                return 'Fee Invoice';
        }
    };

    const overrides = {
        '@field': {
            showOptional: true
        },
        invoiceVatCatagory: {
            visible: vatCustomer

        },
        invoiceVatExclAmt: {
            visible: vatCustomer
        },
        lineVateExempt: {
            visible: vatCustomer,
            value: showVatExempt,
            onValueChange: handleVatExempt
        },
        lineItemsTable: {
            showSearch: false,
            data: lineItemVatData,
            visible: !_.isEmpty(lineItemVatData)
        },
        addLineButton: {
            onClick: addNewLineItem
        },
        totalInclAmount: {
            visible: !_.isEmpty(lineItemVatData),
            // eslint-disable-next-line prefer-template
            value: 'R' + totalInclAmount
        },
        serviceRequestDocumentType: {
            value: getDocumentType()
        },
        documentUpload: {
            path: !_.isUndefined(invoiceVM) ? invoiceVM : null,
            authHeader: authHeader,
            existingDocuments: existingDocuments,
            uploadDocumentTypes: serviceRequestDocumentType,
            serviceRequestID: serviceRequestID,
            setInvoiceVM: setInvoiceVM,
            vendorDocumentType: 'Invoice',
            setInvoiceDocValidated: setInvoiceDocValidated,
            refreshData: refreshData
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            getCurrencyExclValue: getCurrencyValue,
            getCurrencyVatValue: getCurrencyValue,
            getCurrencyInclValue: getCurrencyValue
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            uploadcomponent: UploadComponent
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose} shouldCloseOnOverlayClick={false}>
            <ModalHeader title={translator(messages.addInvoiceTitle)} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={invoiceVM}
                    onModelChange={handleInvoiceVMChange}
                    componentMap={resolvers.resolveComponentMap}
                    callbackMap={resolvers.resolveCallbackMap}
                    classNameMap={resolvers.resolveClassNameMap}
                    overrideProps={overrides}
                    onValueChange={writeValue}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleAddInvoice}
                    disabled={!(invoiceValidated && invoiceDocValidated)}
                >
                    {translator(messages.add)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

AddInvoicePopup.propTypes = {
    isOpen: PropTypes.bool,
    existingDocuments: PropTypes.arrayOf({}),
    serviceRequestID: PropTypes.string,
    vatCustomer: PropTypes.bool,
    requestKind: PropTypes.string,
    onResolve: PropTypes.func,
    onReject: PropTypes.func,
    onAfterClose: PropTypes.func,
    invoiceData: PropTypes.shape({})
};

AddInvoicePopup.defaultProps = {
    onAfterClose: _.noop,
    isOpen: false,
    onResolve: _.noop,
    onReject: _.noop,
    existingDocuments: [],
    serviceRequestID: undefined,
    requestKind: undefined,
    vatCustomer: false,
    invoiceData: {}
};

export default AddInvoicePopup;
